<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAccount } from 'dashboard/composables/useAccount';

const { t } = useI18n();
const { accountScopedRoute } = useAccount();

const steps = computed(() => [
  {
    n: 1,
    title: t('CUSTOM.AI_AGENT.USAGE_GUIDE.STEPS.1.TITLE'),
    desc: t('CUSTOM.AI_AGENT.USAGE_GUIDE.STEPS.1.DESC'),
    cta: t('CUSTOM.AI_AGENT.USAGE_GUIDE.STEPS.1.CTA'),
    to: accountScopedRoute('custom_ai_agent_settings'),
  },
  {
    n: 2,
    title: t('CUSTOM.AI_AGENT.USAGE_GUIDE.STEPS.2.TITLE'),
    desc: t('CUSTOM.AI_AGENT.USAGE_GUIDE.STEPS.2.DESC'),
    cta: t('CUSTOM.AI_AGENT.USAGE_GUIDE.STEPS.2.CTA'),
    // portals_index requires navigationPath (same as Help Center sidebar).
    to: accountScopedRoute('portals_index', {
      navigationPath: 'portals_articles_index',
    }),
  },
  {
    n: 3,
    title: t('CUSTOM.AI_AGENT.USAGE_GUIDE.STEPS.3.TITLE'),
    desc: t('CUSTOM.AI_AGENT.USAGE_GUIDE.STEPS.3.DESC'),
    cta: t('CUSTOM.AI_AGENT.USAGE_GUIDE.STEPS.3.CTA'),
    to: accountScopedRoute('settings_inbox_list'),
  },
]);
</script>

<template>
  <div class="ug-page">
    <header class="ug-header">
      <h1 class="ug-title">{{ t('CUSTOM.AI_AGENT.USAGE_GUIDE.TITLE') }}</h1>
      <p class="ug-sub">{{ t('CUSTOM.AI_AGENT.USAGE_GUIDE.SUBTITLE') }}</p>
    </header>

    <div class="ug-version">{{ t('CUSTOM.AI_AGENT.USAGE_GUIDE.VERSION') }}</div>

    <ol class="ug-steps">
      <li v-for="step in steps" :key="step.n" class="ug-step">
        <div class="ug-badge">{{ step.n }}</div>
        <div class="ug-body">
          <div class="ug-step-title">{{ step.title }}</div>
          <p class="ug-step-desc">{{ step.desc }}</p>
        </div>
        <router-link :to="step.to" class="ug-link">{{ step.cta }} →</router-link>
      </li>
    </ol>
  </div>
</template>

<style scoped>
/* Theme-aware via host next-color CSS variables — no hardcoded hex (PRD §9.6). */
.ug-page { max-width: 760px; padding: 28px 32px; margin: 0 auto; }
.ug-header { margin-bottom: 16px; }
.ug-title { font-size: 20px; font-weight: 700; color: rgb(var(--slate-12)); margin: 0 0 6px; }
.ug-sub { font-size: 13px; line-height: 1.7; color: rgb(var(--slate-11)); margin: 0; }
.ug-version { font-size: 12px; font-weight: 600; color: rgb(var(--blue-11)); background: rgb(var(--blue-3));
  display: inline-block; padding: 5px 12px; border-radius: 999px; margin-bottom: 18px; }
.ug-steps { list-style: none; margin: 0; padding: 0; }
.ug-step { display: flex; align-items: center; gap: 16px; border: 1px solid rgb(var(--slate-5));
  background: rgb(var(--slate-1)); border-radius: 10px; padding: 16px 20px; margin-bottom: 14px; }
.ug-badge { flex-shrink: 0; width: 28px; height: 28px; border-radius: 50%; background: rgb(var(--blue-9));
  color: #fff; font-size: 14px; font-weight: 700; display: flex; align-items: center; justify-content: center; }
.ug-body { flex: 1; }
.ug-step-title { font-size: 14px; font-weight: 600; color: rgb(var(--slate-12)); margin-bottom: 3px; }
.ug-step-desc { font-size: 12px; line-height: 1.6; color: rgb(var(--slate-11)); margin: 0; }
.ug-link { flex-shrink: 0; font-size: 13px; font-weight: 600; color: rgb(var(--blue-11)); text-decoration: none; white-space: nowrap; }
.ug-link:hover { text-decoration: underline; }
</style>
